import React, { memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import ListTableWrapper from '@sharedCR/common/ListTableWrapper';
import FormGeneratorTextFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/formGenerator/FormGeneratorTextFieldCDS';
import DollarCanIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/DollarCanIcon';
import TitleIcon from 'app/shared/componentsRestyled/coreDesignSystem/TitleIcon';
import _ from 'lodash';

import { FieldInputType } from 'constants/fields';
import { OptimalBlueAdjustmentType } from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import { checkIsDirectPendingChange, getPendingChangeKind } from 'utils/pendingChanges';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  selectIsOBEngineType,
  selectSellerSideAdjustments,
  selectTotalLoanAmount,
} from 'store/loanApplications/selectors';

import { defaultForm, pagesPropTypes } from '../MainContent.constants';
import { MANUAL_SELL_PRICE_HINT } from '../SellSideLock/SellSideLock.constants';
import { ContentHeadline } from '../sharedComponents';

import AdjustmentsTooltip from './AdjustmentsTooltip';
import { loanPLDetailsConfig } from './LoanPL.config';
import {
  MANUAL_BUY_SIDE_FIELD_NAMES,
  MANUAL_SELL_SIDE_FIELD_NAMES,
  PERCENTAGE,
} from './LoanPL.constants';
import {
  gainLossStyles,
  loanDetailsStyles,
  profitLossStyles,
  topSectionCustomStyles,
  topSectionStyles,
} from './LoanPL.styles';

const LoanPL = ({
  isLoading,
  form = defaultForm,
  validationErrors,
  handleChange,
  setInForm,
  className,
  pendingChanges,
  setEntityConfig,
  applicationData,
}) => {
  const { isManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_PROFIT_AND_LOSS),
  );

  const isOBEngineType = useSelector(selectIsOBEngineType);
  const totalLoanAmount = useSelector(selectTotalLoanAmount);
  const sellerSideAdjustments = useSelector(selectSellerSideAdjustments);
  const buyerSideAdjustments = applicationData.adjustments;

  const groupedSellerSideAdjustments = useMemo(
    () => _.groupBy(sellerSideAdjustments, 'type'),
    [sellerSideAdjustments],
  );

  const groupedBuyerSideAdjustments = useMemo(
    () => _.groupBy(buyerSideAdjustments, 'type'),
    [buyerSideAdjustments],
  );

  const checkIsAnyFieldChanged = fieldNames =>
    fieldNames.some(fieldName =>
      checkIsDirectPendingChange(_.get(pendingChanges, `profitAndLoss.${fieldName}`)),
    );

  const isAnyManualBuyFieldChanged = checkIsAnyFieldChanged(MANUAL_BUY_SIDE_FIELD_NAMES);
  const isAnyManualSellFieldChanged = checkIsAnyFieldChanged(MANUAL_SELL_SIDE_FIELD_NAMES);
  const isAnyManualFieldChanged = isAnyManualBuyFieldChanged || isAnyManualSellFieldChanged;

  useEffect(() => {
    setEntityConfig(loanPLDetailsConfig);
  }, [setEntityConfig]);

  useEffect(() => {
    setInForm(
      'profitAndLoss.totalSellPrice',
      _.sum([form.profitAndLoss?.netSellPrice, form.profitAndLoss?.srpFromInvestor]),
    );
  }, [setInForm, form.profitAndLoss?.netSellPrice, form.profitAndLoss?.srpFromInvestor]);

  useEffect(() => {
    setInForm(
      'profitAndLoss.totalBuyPrice',
      _.sum([
        form.profitAndLoss?.netBuyPrice,
        form.profitAndLoss?.srpPaidOut,
        form.profitAndLoss?.hedgeCost,
      ]),
    );
  }, [
    setInForm,
    form.profitAndLoss?.netBuyPrice,
    form.profitAndLoss?.srpPaidOut,
    form.profitAndLoss?.hedgeCost,
  ]);

  useEffect(() => {
    const profitLossPercent = _.subtract(
      form.profitAndLoss?.totalSellPrice || 0,
      form.profitAndLoss?.totalBuyPrice || 0,
    );

    setInForm('profitAndLoss.profitLossPercent', profitLossPercent);
  }, [setInForm, form.profitAndLoss?.totalSellPrice, form.profitAndLoss?.totalBuyPrice]);

  useEffect(() => {
    const profitLossPercent = _.subtract(
      form.profitAndLoss?.totalSellPrice || 0,
      form.profitAndLoss?.totalBuyPrice || 0,
    );

    setInForm('profitAndLoss.profitLossDollar', (profitLossPercent * totalLoanAmount) / PERCENTAGE);
  }, [
    setInForm,
    form.profitAndLoss?.totalSellPrice,
    form.profitAndLoss?.totalBuyPrice,
    totalLoanAmount,
  ]);

  return (
    <Box className={className}>
      <ContentHeadline
        iconNode={<TitleIcon icon={DollarCanIcon} iconSize={14} />}
        title="Loan Profit & Loss"
      />

      <Box sx={topSectionStyles}>
        <ListTableWrapper
          isFullWidthDivider
          customStyles={topSectionCustomStyles}
          title="Gain and Loss"
          titleVariant="bold16"
        >
          <Box sx={loanDetailsStyles}>
            <Box sx={gainLossStyles}>
              <div>
                <AdjustmentsTooltip
                  adjustments={groupedBuyerSideAdjustments[OptimalBlueAdjustmentType.PRICE]}
                  label="Net Buy Price"
                />

                <FormGeneratorTextFieldCDS
                  disabled
                  allowNegative={false}
                  decimalScale={3}
                  decimalSeparator="."
                  error={!!validationErrors.profitAndLoss?.netBuyPrice}
                  fieldChangeKind={getPendingChangeKind(
                    _.get(pendingChanges, 'profitAndLoss.netBuyPrice'),
                  )}
                  helperText={validationErrors.profitAndLoss?.netBuyPrice}
                  id="netBuyPrice"
                  name="profitAndLoss.netBuyPrice"
                  type={FieldInputType.FLOAT}
                  value={form.profitAndLoss?.netBuyPrice}
                  variant="outlined"
                  onChange={handleChange}
                />
              </div>
              <div>
                <AdjustmentsTooltip
                  adjustments={groupedBuyerSideAdjustments[OptimalBlueAdjustmentType.SRP]}
                  label="SRP Paid out"
                />

                <FormGeneratorTextFieldCDS
                  allowNegative={false}
                  decimalScale={3}
                  decimalSeparator="."
                  disabled={isLoading || !isManageAllowed || isOBEngineType}
                  error={!!validationErrors.profitAndLoss?.srpPaidOut}
                  fieldChangeKind={getPendingChangeKind(
                    _.get(pendingChanges, 'profitAndLoss.srpPaidOut'),
                  )}
                  helperText={validationErrors.profitAndLoss?.srpPaidOut}
                  id="profitAndLoss.srpPaidOut"
                  name="profitAndLoss.srpPaidOut"
                  type={FieldInputType.FLOAT}
                  value={form.profitAndLoss?.srpPaidOut}
                  variant="outlined"
                  onChange={handleChange}
                />
              </div>
              <div>
                <Typography variant="regular14">Hedge Cost</Typography>
                <FormGeneratorTextFieldCDS
                  allowNegative={false}
                  decimalScale={3}
                  decimalSeparator="."
                  disabled={isLoading || !isManageAllowed}
                  error={!!validationErrors.profitAndLoss?.hedgeCost}
                  fieldChangeKind={getPendingChangeKind(
                    _.get(pendingChanges, 'profitAndLoss.hedgeCost'),
                  )}
                  helperText={validationErrors.profitAndLoss?.hedgeCost}
                  id="profitAndLoss.hedgeCost"
                  name="profitAndLoss.hedgeCost"
                  type={FieldInputType.FLOAT}
                  value={form.profitAndLoss?.hedgeCost}
                  variant="outlined"
                  onChange={handleChange}
                />
              </div>
              <div>
                <Typography variant="regular14">Total Buy Price</Typography>
                <FormGeneratorTextFieldCDS
                  disabled
                  allowNegative={false}
                  decimalScale={3}
                  decimalSeparator="."
                  error={!!validationErrors.profitAndLoss?.totalBuyPrice}
                  helperText={validationErrors.profitAndLoss?.totalBuyPrice}
                  id="profitAndLoss.totalBuyPrice"
                  isFieldChanged={
                    checkIsDirectPendingChange(
                      _.get(pendingChanges, 'profitAndLoss.totalBuyPrice'),
                    ) && isAnyManualBuyFieldChanged
                  }
                  name="profitAndLoss.totalBuyPrice"
                  type={FieldInputType.FLOAT}
                  value={form.profitAndLoss?.totalBuyPrice}
                  variant="outlined"
                  onChange={handleChange}
                />
              </div>
            </Box>

            <Box sx={gainLossStyles}>
              <div>
                <AdjustmentsTooltip
                  adjustments={groupedSellerSideAdjustments[OptimalBlueAdjustmentType.PRICE]}
                  label="Net Sell Price"
                />

                <FormGeneratorTextFieldCDS
                  allowNegative={false}
                  decimalScale={3}
                  decimalSeparator="."
                  disabled={isLoading || !isManageAllowed}
                  error={!!validationErrors.profitAndLoss?.netSellPrice}
                  fieldChangeKind={getPendingChangeKind(
                    _.get(pendingChanges, 'profitAndLoss.netSellPrice'),
                  )}
                  helperText={
                    validationErrors.profitAndLoss?.netSellPrice ??
                    (applicationData.sellSideLock?.isSellPriceManualOverride
                      ? MANUAL_SELL_PRICE_HINT
                      : undefined)
                  }
                  id="profitAndLoss.netSellPrice"
                  name="profitAndLoss.netSellPrice"
                  type={FieldInputType.FLOAT}
                  value={form.profitAndLoss?.netSellPrice}
                  variant="outlined"
                  onChange={handleChange}
                />
              </div>
              <div>
                <AdjustmentsTooltip
                  adjustments={groupedSellerSideAdjustments[OptimalBlueAdjustmentType.SRP]}
                  label="SRP from Investor"
                />

                <FormGeneratorTextFieldCDS
                  allowNegative={false}
                  decimalScale={3}
                  decimalSeparator="."
                  disabled={isLoading || !isManageAllowed}
                  error={!!validationErrors.profitAndLoss?.srpFromInvestor}
                  fieldChangeKind={getPendingChangeKind(
                    _.get(pendingChanges, 'profitAndLoss.srpFromInvestor'),
                  )}
                  helperText={validationErrors.profitAndLoss?.srpFromInvestor}
                  id="profitAndLoss.srpFromInvestor"
                  name="profitAndLoss.srpFromInvestor"
                  type={FieldInputType.FLOAT}
                  value={form.profitAndLoss?.srpFromInvestor}
                  variant="outlined"
                  onChange={handleChange}
                />
              </div>
              <div />
              <div>
                <Typography variant="regular14">Total Sell Price</Typography>
                <FormGeneratorTextFieldCDS
                  disabled
                  allowNegative={false}
                  decimalScale={3}
                  decimalSeparator="."
                  error={!!validationErrors.profitAndLoss?.totalSellPrice}
                  helperText={validationErrors.profitAndLoss?.totalSellPrice}
                  id="profitAndLoss.totalSellPrice"
                  isFieldChanged={
                    checkIsDirectPendingChange(
                      _.get(pendingChanges, 'profitAndLoss.totalSellPrice'),
                    ) && isAnyManualSellFieldChanged
                  }
                  name="profitAndLoss.totalSellPrice"
                  type={FieldInputType.FLOAT}
                  value={form.profitAndLoss?.totalSellPrice}
                  variant="outlined"
                  onChange={handleChange}
                />
              </div>
            </Box>
          </Box>

          <Box sx={loanDetailsStyles}>
            <Box sx={profitLossStyles}>
              <FormGeneratorTextFieldCDS
                allowNegative
                disabled
                decimalScale={3}
                decimalSeparator="."
                error={!!validationErrors.profitAndLoss?.profitLossPercent}
                helperText={validationErrors.profitAndLoss?.profitLossPercent}
                id="profitAndLoss.profitLossPercent"
                isFieldChanged={
                  checkIsDirectPendingChange(
                    _.get(pendingChanges, 'profitAndLoss.profitLossPercent'),
                  ) && isAnyManualFieldChanged
                }
                label="Profit / Loss %"
                name="profitAndLoss.profitLossPercent"
                type={FieldInputType.PERCENTAGE_RESTYLED}
                value={form.profitAndLoss?.profitLossPercent}
                variant="outlined"
                onChange={handleChange}
              />

              <FormGeneratorTextFieldCDS
                allowNegative
                disabled
                decimalScale={2}
                decimalSeparator="."
                error={!!validationErrors.profitAndLoss?.profitLossDollar}
                helperText={validationErrors.profitAndLoss?.profitLossDollar}
                id="profitAndLoss.profitLossDollar"
                isFieldChanged={
                  checkIsDirectPendingChange(
                    _.get(pendingChanges, 'profitAndLoss.profitLossDollar'),
                  ) && isAnyManualFieldChanged
                }
                label="Profit / Loss $"
                name="profitAndLoss.profitLossDollar"
                type="currency"
                value={form.profitAndLoss?.profitLossDollar}
                variant="outlined"
                onChange={handleChange}
              />
            </Box>
          </Box>
        </ListTableWrapper>
      </Box>
    </Box>
  );
};

LoanPL.propTypes = pagesPropTypes;

export default memo(LoanPL);
